import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ReactionType } from '@/components/ReactionPicker';
import { sendLikeNotification } from '@/services/triggerNotification';

interface PostReactions {
  total_count: number;
  user_reaction: ReactionType | null;
  reaction_counts: Array<{
    reaction_type: ReactionType;
    count: number;
  }>;
}

const emptyReactions: PostReactions = {
  total_count: 0,
  user_reaction: null,
  reaction_counts: [],
};

// Pure helpers used for optimistic cache updates
const applyOptimisticAdd = (
  current: PostReactions | undefined,
  reactionType: ReactionType
): PostReactions => {
  const base = current ?? emptyReactions;
  const counts = base.reaction_counts.map(r => ({ ...r }));

  const previous = base.user_reaction;
  if (previous) {
    const prevEntry = counts.find(r => r.reaction_type === previous);
    if (prevEntry) prevEntry.count -= 1;
  }

  const nextEntry = counts.find(r => r.reaction_type === reactionType);
  if (nextEntry) {
    nextEntry.count += 1;
  } else {
    counts.push({ reaction_type: reactionType, count: 1 });
  }

  return {
    total_count: previous ? base.total_count : base.total_count + 1,
    user_reaction: reactionType,
    reaction_counts: counts.filter(r => r.count > 0).sort((a, b) => b.count - a.count),
  };
};

const applyOptimisticRemove = (current: PostReactions | undefined): PostReactions => {
  const base = current ?? emptyReactions;
  if (!base.user_reaction) return base;

  const counts = base.reaction_counts.map(r => ({ ...r }));
  const entry = counts.find(r => r.reaction_type === base.user_reaction);
  if (entry) entry.count -= 1;

  return {
    total_count: Math.max(0, base.total_count - 1),
    user_reaction: null,
    reaction_counts: counts.filter(r => r.count > 0).sort((a, b) => b.count - a.count),
  };
};

// Fetch post reactions data
export const usePostReactions = (postId: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['post-reactions', postId];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async (): Promise<PostReactions> => {
      // Get reaction counts
      const { data: reactions, error: reactionsError } = await supabase
        .from('reactions')
        .select('reaction_type, user_id')
        .eq('post_id', postId);

      if (reactionsError) throw reactionsError;

      // Calculate counts and user's reaction
      const reactionCounts = reactions?.reduce((acc, reaction) => {
        const existing = acc.find(r => r.reaction_type === reaction.reaction_type);
        if (existing) {
          existing.count++;
        } else {
          acc.push({ reaction_type: reaction.reaction_type as ReactionType, count: 1 });
        }
        return acc;
      }, [] as Array<{ reaction_type: ReactionType; count: number }>) || [];

      const userReaction = user ? 
        reactions?.find(r => r.user_id === user.id)?.reaction_type as ReactionType || null : 
        null;

      const totalCount = reactions?.length || 0;

      return {
        total_count: totalCount,
        user_reaction: userReaction,
        reaction_counts: reactionCounts.sort((a, b) => b.count - a.count)
      };
    },
    enabled: !!postId,
  });

  // Fire-and-forget notification, kept off the critical path
  const notifyPostOwner = (reactorId: string) => {
    void (async () => {
      try {
        const { data: post } = await supabase
          .from('memory_posts')
          .select('user_id')
          .eq('id', postId)
          .single();

        if (post && post.user_id !== reactorId) {
          await sendLikeNotification(post.user_id, reactorId, postId);
        }
      } catch (notificationError) {
        console.error('Error sending reaction notification:', notificationError);
        // Reaction already succeeded; notification failure must not break the flow
      }
    })();
  };

  const addReaction = useMutation({
    mutationFn: async (reactionType: ReactionType) => {
      if (!user) throw new Error('Authentication required');

      const { error } = await supabase
        .from('reactions')
        .upsert({
          user_id: user.id,
          post_id: postId,
          reaction_type: reactionType
        }, {
          onConflict: 'user_id,post_id'
        });

      if (error) throw error;

      notifyPostOwner(user.id);
    },
    onMutate: async (reactionType: ReactionType) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PostReactions>(queryKey);
      queryClient.setQueryData<PostReactions>(queryKey, applyOptimisticAdd(previous, reactionType));
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const removeReaction = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Authentication required');

      const { error } = await supabase
        .from('reactions')
        .delete()
        .eq('user_id', user.id)
        .eq('post_id', postId);

      if (error) throw error;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PostReactions>(queryKey);
      queryClient.setQueryData<PostReactions>(queryKey, applyOptimisticRemove(previous));
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    data,
    isLoading,
    error,
    addReaction: addReaction.mutateAsync,
    removeReaction: removeReaction.mutateAsync,
    isAddingReaction: addReaction.isPending,
    isRemovingReaction: removeReaction.isPending,
  };
};
